// ITER262 FE — City Intelligence · Phase 1 frontend.
//
// Consultant-grade discovery experience built on the
// `POST /api/expansion-intelligence/city-intelligence/seed` endpoint.
// No backend changes; reads the response verbatim and renders the
// already-collected demand profile, scores, recommendation block and
// provenance into a guided five-screen flow:
//
//   1) Concept Discovery        — guided concept + city intake
//   2) Discovery Progress       — animated engine stages
//   3) Decision Journey         — narrowing funnel with reasons
//   4) Opportunity Explorer     — ranked candidate cards
//   5) Neighbourhood Detail     — full intelligence + hand-off
//
// The hand-off into existing Site Intelligence is via sessionStorage
// (`ei_prefill_address`) which `ExpansionIntelligenceHome.js` reads on
// mount and skips the landing gate accordingly.
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Sparkles, ArrowRight, ArrowLeft, MapPin, Compass, Users, Briefcase,
  GraduationCap, Sun, Moon, Heart, Sparkle, ChefHat, Building2, Plane,
  CheckCircle2, Loader2, XCircle, AlertTriangle, ShieldCheck,
  TrendingUp, TrendingDown, Activity, FileText, Download,
} from 'lucide-react';
import {
  getSession, mergeSession, clearSession,
} from '../utils/expansionSession';
import BriefRankedFieldsAdapter from '../components/brief/BriefRankedFieldsAdapter';

const API = process.env.REACT_APP_BACKEND_URL;

/* ────────────────────────────────────────────────────────────────────
   Concept vocabulary — kept in sync with the legacy EI brief
   vocabulary so a saved brief on the same tenant continues to merge
   cleanly server-side (use_saved_brief = true).
   ──────────────────────────────────────────────────────────────────── */
const CUISINES = [
  'Indian', 'South Indian', 'Cafe', 'Italian', 'Chinese', 'Pan-Asian',
  'QSR', 'Dessert', 'Bakery', 'Mexican', 'Japanese', 'Korean', 'Thai',
  'Mediterranean', 'Middle Eastern', 'Vegetarian',
];
const FORMATS = [
  'Fine Dine', 'Casual Dine / Other', 'Cafe', 'QSR',
  'Bar', 'Bakery', 'Dessert', 'Cloud Kitchen',
];
const TARGET_SEGMENTS = [
  { v: 'students',            l: 'Students',                Icon: GraduationCap },
  { v: 'young_professionals', l: 'Young professionals',     Icon: Briefcase },
  { v: 'corporate_lunch',     l: 'Corporate lunch crowd',   Icon: Building2 },
  { v: 'business_class',      l: 'Business / executives',   Icon: Briefcase },
  { v: 'families',            l: 'Families',                Icon: Users },
  { v: 'affluent',            l: 'Affluent diners',         Icon: Sparkle },
  { v: 'value_seekers',       l: 'Value seekers',           Icon: Activity },
  { v: 'late_night_diners',   l: 'Late-night diners',       Icon: Moon },
  { v: 'nightlife_bar_crowd', l: 'Nightlife / bar crowd',   Icon: Moon },
  { v: 'health_conscious',    l: 'Health-conscious',        Icon: Heart },
  { v: 'tourists',            l: 'Tourists',                Icon: Plane },
];

/* ────────────────────────────────────────────────────────────────────
   Small UI primitives
   ──────────────────────────────────────────────────────────────────── */
function Chip({ active, onClick, children, testid, Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group inline-flex items-center gap-2 px-4 py-2.5 rounded-full
        border transition-all duration-200 text-sm font-medium
        ${active
          ? 'bg-slate-900 text-amber-300 border-slate-900 shadow-sm'
          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:text-slate-900'}`}
    >
      {Icon ? <Icon className="w-4 h-4" /> : null}
      <span>{children}</span>
    </button>
  );
}

function SectionLabel({ children, sublabel }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        {children}
      </div>
      {sublabel && (
        <div className="text-sm text-slate-500 mt-1">{sublabel}</div>
      )}
    </div>
  );
}

function Hero({ stepIndex }) {
  return (
    <header className="flex items-start gap-4 mb-10">
      <div className="p-3 rounded-2xl bg-slate-900 text-amber-300 shrink-0">
        <Compass className="w-7 h-7" />
      </div>
      <div className="flex-1 min-w-0">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
          City Intelligence
        </h1>
        <p className="text-base text-slate-600 mt-2 max-w-2xl">
          From concept to a short-list of investment-ready neighbourhoods —
          guided by evidence, not opinion.
        </p>
        <Stepper stepIndex={stepIndex} />
      </div>
    </header>
  );
}

const STEPS = [
  { key: 'concept',     l: 'Concept' },
  { key: 'discovering', l: 'Discovery' },
  { key: 'decision',    l: 'Decision journey' },
  { key: 'explorer',    l: 'Opportunities' },
  { key: 'detail',      l: 'Neighbourhood detail' },
];

function Stepper({ stepIndex }) {
  return (
    <ol className="flex flex-wrap gap-x-6 gap-y-2 mt-5" data-testid="city-intel-stepper">
      {STEPS.map((s, i) => {
        const done = i < stepIndex;
        const active = i === stepIndex;
        return (
          <li key={s.key} className="flex items-center gap-2 text-sm">
            <span
              className={`inline-flex w-6 h-6 items-center justify-center rounded-full text-[11px] font-semibold border
                ${done ? 'bg-emerald-500 text-white border-emerald-500'
                  : active ? 'bg-slate-900 text-amber-300 border-slate-900'
                  : 'bg-white text-slate-400 border-slate-200'}`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span className={active ? 'text-slate-900 font-medium'
                                    : done ? 'text-slate-600' : 'text-slate-400'}>
              {s.l}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SCREEN 1 — Concept Discovery
   ════════════════════════════════════════════════════════════════════ */
function PreferredAreasField({ form, setForm }) {
  const [draft, setDraft] = useState('');
  const list = form.preferred_areas || [];
  const isFull = list.length >= PREFERRED_AREAS_MAX;

  const addChip = (raw) => {
    const name = (raw || '').trim().replace(/,+$/, '').trim();
    if (!name) return;
    setForm(f => {
      const cur = f.preferred_areas || [];
      // Case-insensitive dedupe.
      if (cur.some(x => x.toLowerCase() === name.toLowerCase())) return f;
      if (cur.length >= PREFERRED_AREAS_MAX) return f;
      return { ...f, preferred_areas: [...cur, name.slice(0, 120)] };
    });
    setDraft('');
  };

  const removeChip = (idx) => {
    setForm(f => ({
      ...f,
      preferred_areas: (f.preferred_areas || []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <section data-testid="city-intel-preferred-areas-section">
      <SectionLabel sublabel="Add specific areas you'd like the engine to assess. They run through the same demand and competition gates as all other candidates — no special treatment.">
        PREFERRED AREAS TO EVALUATE (OPTIONAL)
      </SectionLabel>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="city-intel-preferred-areas-chips">
        {list.map((area, i) => (
          <span
            key={`${area}-${i}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-slate-900 text-amber-200 border border-slate-900"
            data-testid={`city-intel-preferred-area-chip-${i}`}
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>{area}</span>
            <button
              type="button"
              onClick={() => removeChip(i)}
              className="ml-1 text-amber-200/70 hover:text-white"
              aria-label={`Remove ${area}`}
              data-testid={`city-intel-preferred-area-remove-${i}`}
            >
              <XCircle className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
      <Input
        type="text"
        value={draft}
        onChange={e => {
          const v = e.target.value;
          // Support comma-separated entry.
          if (v.includes(',')) {
            const parts = v.split(',');
            parts.slice(0, -1).forEach(addChip);
            setDraft(parts[parts.length - 1] || '');
            return;
          }
          setDraft(v);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addChip(draft);
          } else if (e.key === 'Backspace' && !draft && list.length > 0) {
            removeChip(list.length - 1);
          }
        }}
        onBlur={() => { if (draft) addChip(draft); }}
        placeholder={isFull
          ? `Maximum ${PREFERRED_AREAS_MAX} preferred areas`
          : 'Type an area and press Enter — e.g. Vasai West'}
        disabled={isFull}
        title={isFull ? `Maximum ${PREFERRED_AREAS_MAX} preferred areas` : undefined}
        data-testid="city-intel-preferred-areas-input"
      />
      {isFull && (
        <div className="text-xs text-slate-500 mt-1.5" data-testid="city-intel-preferred-areas-full-note">
          Maximum {PREFERRED_AREAS_MAX} preferred areas. Remove one to add another.
        </div>
      )}
    </section>
  );
}

function ConceptDiscoveryScreen({ form, setForm, onSubmit, savedBrief, taxonomy, onClearBrief }) {
  const toggleListItem = (key, val) => {
    setForm(f => {
      const cur = new Set(f[key] || []);
      cur.has(val) ? cur.delete(val) : cur.add(val);
      return { ...f, [key]: Array.from(cur) };
    });
  };
  const setScalar = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // 4-Axis Addendum — resolve chip labels from DB taxonomy with a
  // graceful fallback to the legacy hardcoded lists while the fetch
  // is in flight (first paint) or if the endpoint is unreachable.
  const cuisineChips = (taxonomy?.cuisines?.length
    ? taxonomy.cuisines.map(c => ({ v: c.id, l: c.display_name }))
    : CUISINES.map(c => ({ v: c, l: c })));
  const formatChips = (taxonomy?.formats?.length
    ? taxonomy.formats.map(f => ({ v: f.id, l: f.display_name }))
    : FORMATS.map(f => ({ v: f, l: f })));
  const tierChips = taxonomy?.tiers || [
    { id: 'premium',    display_name: 'Premium' },
    { id: 'mid_market', display_name: 'Mid-market' },
    { id: 'budget_qsr', display_name: 'Budget / QSR' },
  ];
  const dietaryChips = taxonomy?.dietary || [];

  const canSubmit = useMemo(() => {
    return !!form.business_objective?.trim() && !!form.city?.trim();
  }, [form.business_objective, form.city]);

  return (
    <div className="space-y-10" data-testid="city-intel-concept-screen">
      {savedBrief?.business_objective && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <span className="font-medium">A saved brief is on file</span>
            <span className="text-amber-800"> — &ldquo;{(savedBrief.business_objective || '').slice(0, 100)}…&rdquo;.
            We&apos;ll merge any fields you leave blank below.</span>
          </div>
        </div>
      )}

      <section>
        <SectionLabel sublabel="What you are trying to build">RESTAURANT CONCEPT</SectionLabel>
        <Textarea
          rows={3}
          maxLength={400}
          placeholder="e.g. A premium all-day Italian cafe focused on corporate lunch and weekend brunch, ₹600 for two."
          value={form.business_objective}
          onChange={e => {
            // BRIEF ISOLATION FIX — typing a new concept invalidates
            // any prior expansion session. Clearing it here prevents
            // stale cuisines/formats/segments from a previous run
            // from silently persisting into the current brief.
            clearSession();
            setForm(f => ({ ...f, business_objective: e.target.value }));
          }}
          className="text-base"
          data-testid="city-intel-input-objective"
        />
      </section>

      <section>
        {/* ITER283c — Cuisines + Formats + Segments consolidated into
            the shared ranked-brief component. Broad cuisine list,
            pick-then-order top-5 for cuisines and segments,
            format multi-select (max 2), free-text custom cuisines. */}
        <BriefRankedFieldsAdapter
          form={form}
          setForm={setForm}
          formatOptions={formatChips.map(f => ({ id: f.v, label: f.l }))}
          segmentOptions={TARGET_SEGMENTS.map(s => ({ id: s.v, label: s.l }))}
        />
      </section>

      <section>
        <SectionLabel sublabel="Where does the concept sit on price?">
          PRICE POSITIONING
        </SectionLabel>
        <div className="flex flex-wrap gap-2">
          {tierChips.map(({ id, display_name }) => (
            <Chip
              key={id}
              active={form.tier === id}
              onClick={() => setScalar('tier', form.tier === id ? '' : id)}
              testid={`city-intel-tier-${id.replace(/_/g, '-')}`}
            >
              {display_name}
            </Chip>
          ))}
        </div>
      </section>

      {dietaryChips.length > 0 && (
        <section>
          <SectionLabel sublabel="Optional — filters direct-competitor counts, not the ranking">
            DIETARY
          </SectionLabel>
          <div className="flex flex-wrap gap-2">
            {dietaryChips.map(({ id, display_name }) => (
              <Chip
                key={id}
                active={(form.dietary || []).includes(id)}
                onClick={() => toggleListItem('dietary', id)}
                testid={`city-intel-dietary-${id.replace(/_/g, '-')}`}
              >
                {display_name}
              </Chip>
            ))}
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <SectionLabel sublabel="Average bill per couple (₹)">PRICE FOR TWO</SectionLabel>
          <Input
            type="number"
            inputMode="numeric"
            placeholder="e.g. 600"
            value={form.price_for_two}
            onChange={e => setForm(f => ({ ...f, price_for_two: e.target.value }))}
            data-testid="city-intel-input-price"
          />
        </div>
        <div>
          <SectionLabel sublabel="Which city are you considering?">CITY</SectionLabel>
          <Input
            type="text"
            placeholder="e.g. Bangalore"
            value={form.city}
            onChange={e => {
              // BRIEF ISOLATION FIX — changing city clears any prior
              // expansion session so a new run isn't contaminated by
              // the previous city's cached brief fields.
              clearSession();
              setForm(f => ({ ...f, city: e.target.value }));
            }}
            data-testid="city-intel-input-city"
          />
        </div>
      </section>

      <PreferredAreasField form={form} setForm={setForm} />

      <section>
        <SectionLabel sublabel="Optional — nuance about your ideal customer (1–2 sentences)">
          TARGET AUDIENCE DESCRIPTION
        </SectionLabel>
        <Textarea
          rows={2}
          maxLength={400}
          placeholder="e.g. Tech professionals 25–40 looking for healthy lunch options near coworking hubs."
          value={form.target_audience_description}
          onChange={e => setForm(f => ({ ...f, target_audience_description: e.target.value }))}
          data-testid="city-intel-input-audience"
        />
      </section>

      <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-slate-500 max-w-md">
          Your concept is matched against live evidence — restaurant
          density, anchor POIs, segment fit and saturation — and ranked
          across 8–12 candidate neighbourhoods.
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // ITER283c-fix — Explicit clear. Wipes ALL brief fields
              // (concept, cuisines, formats, segments, price, city,
              // dietary, tier, audience) AND the sessionStorage
              // session so no stale value repopulates on the next
              // render.
              if (!window.confirm(
                'Clear the entire brief? This resets concept, cuisines, formats, segments, price, city and dietary picks.'
              )) return;
              onClearBrief && onClearBrief();
            }}
            data-testid="city-intel-clear-brief-btn"
            className="text-slate-600 border-slate-300"
          >
            Clear brief
          </Button>
          <Button
            size="lg"
            disabled={!canSubmit}
            onClick={onSubmit}
            data-testid="city-intel-find-locations-btn"
            className="bg-slate-900 hover:bg-slate-800 text-amber-300 font-medium px-7 h-12 rounded-full"
          >
            Find best locations <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SCREEN 2 — Discovery Progress (no spinner; engine stages)
   ════════════════════════════════════════════════════════════════════ */
const DISCOVERY_STAGES = [
  { l: 'Understanding your concept',           d: 'Parsing cuisine, format, price band and target segments.' },
  { l: 'Identifying commercial neighbourhoods', d: 'Asking the strongest available model to propose 8–12 micro-localities.' },
  { l: 'Validating neighbourhoods',            d: 'Geocoding each name and confirming baseline F&B activity within 1.5 km.' },
  { l: 'Building demand profiles',             d: 'Scanning anchor POIs for student, professional, family, affluence, corporate, nightlife, tourist & wellness density.' },
  { l: 'Measuring proven demand',              d: 'Crowd-pull from F&B density and anchor density (0–100).' },
  { l: 'Analysing competition',                d: 'Saturation ratio: concept-matching competitors ÷ total F&B.' },
  { l: 'Ranking opportunities',                d: 'Composite ranking with deterministic recommendation rules.' },
];

function DiscoveryProgressScreen({ requestStartedAt, requestDone, requestError, onRetry, onBack }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 700);
    return () => clearInterval(id);
  }, []);
  // Pace the stages — 700 ms each unless the response has arrived first.
  // Once the response arrives, snap ALL stages to done.
  const stageIndex = requestDone ? DISCOVERY_STAGES.length
                                  : Math.min(DISCOVERY_STAGES.length - 1, tick);
  const pct = Math.round(((stageIndex + (requestDone ? 1 : 0)) /
                            DISCOVERY_STAGES.length) * 100);
  const elapsed = Math.max(0, Math.floor((Date.now() - requestStartedAt) / 1000));

  if (requestError) {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="city-intel-discovery-error">
        <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-6 flex items-start gap-3">
          <XCircle className="w-6 h-6 text-rose-600 mt-0.5 shrink-0" />
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-rose-900">
              The engine couldn&apos;t complete the discovery
            </h3>
            <p className="text-sm text-rose-800">{String(requestError)}</p>
            <div className="flex items-center gap-3 pt-2">
              <Button variant="outline" onClick={onBack}
                      data-testid="city-intel-discovery-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to concept
              </Button>
              <Button onClick={onRetry}
                      data-testid="city-intel-discovery-retry-btn"
                      className="bg-slate-900 hover:bg-slate-800 text-amber-300">
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="city-intel-discovery-screen">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-medium text-slate-900">
          Working on your concept...
        </h2>
        <span className="text-sm text-slate-500 tabular-nums">{elapsed}s elapsed</span>
      </div>
      <Progress value={pct} className="h-1.5" data-testid="city-intel-discovery-progress" />
      <ol className="space-y-1 pt-2">
        {DISCOVERY_STAGES.map((s, i) => {
          const done = i < stageIndex || requestDone;
          const active = i === stageIndex && !requestDone;
          return (
            <li key={s.l}
                className={`flex items-start gap-4 rounded-xl p-4 transition-colors
                  ${active ? 'bg-slate-50' : 'bg-white'}`}>
              <div className="mt-0.5 w-6 h-6 shrink-0 flex items-center justify-center">
                {done
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  : active
                    ? <Loader2 className="w-5 h-5 text-slate-700 animate-spin" />
                    : <span className="w-2 h-2 rounded-full bg-slate-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium
                    ${done ? 'text-slate-900' : active ? 'text-slate-900' : 'text-slate-400'}`}>
                  {s.l}
                </div>
                <div className={`text-xs mt-0.5
                    ${done || active ? 'text-slate-500' : 'text-slate-300'}`}>
                  {s.d}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SCREEN 3 — Decision Journey (narrowing funnel)
   ════════════════════════════════════════════════════════════════════ */
/* ────────────────────────────────────────────────────────────────────
   ITER271 — Generate Consultant Report
   Lightweight modal: pre-fill prepared_for + project_name from
   tenant/brief data; let the user override; POST the result blob to
   the new /reports/generate endpoint; trigger a browser download.
   ────────────────────────────────────────────────────────────────── */
function GenerateReportButton({ result, brief }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const defaultProject = useMemo(() => {
    const city = result?.city || '';
    const obj  = (brief?.business_objective || '').slice(0, 50);
    return obj ? `${city} · ${obj}` : city;
  }, [result, brief]);
  const [preparedFor, setPreparedFor] = useState(
    user?.tenant_name || user?.full_name || 'Internal Expansion Review'
  );
  const [projectName, setProjectName] = useState(defaultProject);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/api/expansion-intelligence/city-intelligence/reports/generate`,
        { result, brief, prepared_for: preparedFor, project_name: projectName },
        {
          headers: { Authorization: `Bearer ${token}`,
                      'Content-Type': 'application/json' },
          responseType: 'blob',
          timeout: 60000,
        },
      );
      // Trigger download
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Filename hint from content-disposition; fall back to a clean default.
      const cd = r.headers['content-disposition'] || '';
      const m  = /filename="([^"]+)"/.exec(cd);
      a.download = (m && m[1]) ||
        `City-Intelligence-${(result?.city || 'Report').replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setOpen(false);
    } catch (err) {
      // err.response.data is a Blob — try to read it as JSON for the message
      let msg = 'Failed to generate the report.';
      try {
        if (err.response?.data instanceof Blob) {
          const txt = await err.response.data.text();
          const j   = JSON.parse(txt);
          msg = j.detail?.message || j.detail || msg;
        } else {
          msg = err.response?.data?.detail?.message || err.message || msg;
        }
      } catch { /* keep default */ }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        size="lg"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="generate-consultant-report-btn"
        className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-amber-300 h-12 px-5 rounded-full">
        <FileText className="w-4 h-4 mr-2" />
        Generate Consultant Report
      </Button>
    );
  }

  return (
    <>
      <Button size="lg" variant="outline" onClick={() => setOpen(false)}
              className="h-12 px-5 rounded-full">
        Hide
      </Button>
      <div
        data-testid="generate-consultant-report-modal"
        onClick={() => !busy && setOpen(false)}
        className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
        <div
          onClick={e => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-amber-600 font-semibold">
              Consultant Report
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mt-1">
              Generate a board-ready PDF
            </h3>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
              We&apos;ll produce a fully-branded report — Executive Recommendation,
              decision funnel, top picks, detailed candidate profiles, full ranking
              and methodology. Edit the cover fields below if you want.
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Prepared for
              </label>
              <Input
                value={preparedFor}
                onChange={e => setPreparedFor(e.target.value)}
                data-testid="report-prepared-for-input"
                placeholder="e.g. Board Review · Q1 2026"
                className="mt-1.5"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Project name
              </label>
              <Input
                value={projectName}
                onChange={e => setProjectName(e.target.value)}
                data-testid="report-project-name-input"
                placeholder="e.g. Surat Expansion · Premium Dining"
                className="mt-1.5"
              />
            </div>
          </div>
          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={generate}
              disabled={busy}
              data-testid="report-generate-confirm-btn"
              className="bg-slate-900 hover:bg-slate-800 text-amber-300">
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download PDF</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

function DecisionJourneyScreen({ result, onContinue, onBack, onSelectCandidate }) {
  // ITER268 — Each funnel step has bespoke expansion content sourced
  // ENTIRELY from existing API fields. No new backend data.
  const meta = result?.meta || {};
  const candidates = result?.candidates || [];
  const rejected   = result?.rejected   || [];
  const truncated  = result?.truncated  || [];

  const conceptMatched = candidates.filter(c => c.demand?.verdict !== 'FAIL').length;
  const recommendedCount = Math.min(4, candidates.length);
  const seededCount = meta.seed_count
                       || (candidates.length + rejected.length + truncated.length);

  const funnel = [
    {
      key: 'seeded',
      label: `${seededCount} neighbourhoods considered`,
      sub: 'Proposed by the strongest available LLM, conditioned on your concept and target segments.',
      renderDetails: () =>
        <Step1Details candidates={candidates} rejected={rejected} truncated={truncated} />,
    },
    {
      key: 'validated',
      label: `${meta.validated_count ?? candidates.length} commercially active`,
      sub: 'Geocoded and confirmed to carry a baseline F&B presence within the catchment.',
      renderDetails: () =>
        <Step2Details candidates={candidates} rejected={rejected} truncated={truncated} />,
    },
    {
      key: 'matched',
      label: `${conceptMatched} matched your concept`,
      sub: 'Passed the demand-profile gate against your target segments (PASS or SOFT_PENALTY).',
      renderDetails: () => <Step3Details candidates={candidates} />,
    },
    {
      key: 'recommended',
      label: `${recommendedCount} recommended`,
      sub: 'Ranked by composite score — crowd-pull × demand penalty × saturation modifier.',
      tone: 'ok',
      renderDetails: () =>
        <Step4Details candidates={candidates} onSelectCandidate={onSelectCandidate} />,
    },
  ];

  return (
    <div className="space-y-8 max-w-4xl" data-testid="city-intel-decision-screen">
      <div>
        <h2 className="text-2xl font-medium text-slate-900">
          The narrowing journey
        </h2>
        <p className="text-base text-slate-600 mt-2 max-w-2xl">
          Here&apos;s how the engine filtered candidates for {result?.city}.
          Each stage is expandable so you can see exactly why a name was kept or dropped.
        </p>
      </div>

      <ol className="space-y-3" data-testid="city-intel-decision-funnel">
        {funnel.map((f, idx) => (
          <FunnelStage key={f.key} stage={f} index={idx} />
        ))}
      </ol>

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={onBack}
                data-testid="city-intel-decision-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="flex items-center gap-3">
          <GenerateReportButton result={result} brief={result?.brief} />
          <Button size="lg" onClick={onContinue}
                  data-testid="city-intel-decision-continue-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-amber-300 px-7 h-12 rounded-full">
            See the {recommendedCount} recommendations <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function FunnelStage({ stage, index }) {
  const [open, setOpen] = useState(false);
  const hasDetails = typeof stage.renderDetails === 'function';
  return (
    <li
      data-testid={`city-intel-funnel-stage-${stage.key}`}
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => hasDetails && setOpen(o => !o)}
        data-testid={`city-intel-funnel-stage-${stage.key}-toggle`}
        className={`w-full flex items-center gap-4 p-5 text-left
          ${hasDetails ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
      >
        <div className="w-8 h-8 shrink-0 rounded-full bg-slate-900 text-amber-300
                          flex items-center justify-center text-sm font-semibold">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-base font-medium text-slate-900">{stage.label}</div>
          <div className="text-sm text-slate-500 mt-0.5">{stage.sub}</div>
        </div>
        {hasDetails && (
          <span className="text-xs text-slate-500 font-medium">
            {open ? 'Hide' : 'Show details'}
          </span>
        )}
      </button>
      {open && hasDetails && (
        <div className="border-t border-slate-100 p-5 bg-slate-50/40"
             data-testid={`city-intel-funnel-stage-${stage.key}-details`}>
          {stage.renderDetails()}
        </div>
      )}
    </li>
  );
}

/* ─── Step 1 — full seed list with LLM rationale ─────────────────── */
function Step1Details({ candidates, rejected, truncated }) {
  // Reconstruct the original LLM seed list as the union of all three
  // arrays. Each item carries `name` + (in most cases) `rationale`.
  const all = [...(candidates || []), ...(rejected || []), ...(truncated || [])];
  if (all.length === 0) {
    return <div className="text-sm text-slate-500 italic">No seed list available.</div>;
  }
  const missingRationaleCount = all.filter(c => !c.rationale).length;
  return (
    <div className="space-y-2.5">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
        LLM seed list · {all.length}
      </div>
      <ul className="space-y-2">
        {all.map((c, i) => (
          <li key={`${c.name}-${i}`}
              data-testid={`city-intel-step1-seed-${i}`}
              className="flex items-start gap-3 text-sm">
            <span className="font-semibold text-slate-900 min-w-[7rem] shrink-0">
              {c.name}
            </span>
            {c.rationale ? (
              <span className="text-slate-700">{c.rationale}</span>
            ) : (
              <span className="text-slate-400 italic">rationale not returned</span>
            )}
            {typeof c.confidence === 'number' && (
              <span className="ml-auto text-[11px] tabular-nums text-slate-400 shrink-0">
                conf {Math.round(c.confidence * 100)}%
              </span>
            )}
          </li>
        ))}
      </ul>
      {missingRationaleCount > 0 && (
        <div className="pt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {missingRationaleCount} of {all.length} candidates lack an LLM rationale string.
          Backend gap — to be filled in a follow-up iteration.
        </div>
      )}
    </div>
  );
}

/* ─── Step 2 — validated + reason-categorized rejections ─────────── */
function Step2Details({ candidates, rejected, truncated }) {
  // Categorize rejections by reason for visually distinct treatment.
  const REJECTION_CLASSES = {
    geocoding_failed: {
      label: 'Could not be located',
      sub:   'Google Places could not resolve the neighbourhood name to a precise location.',
      icon:  XCircle,
      iconCls: 'text-rose-500',
      bgCls:   'bg-rose-50 border-rose-200',
      tooltipCls: 'text-rose-700',
    },
    insufficient_fnb: {
      label: 'Low commercial activity',
      sub:   'Geocoded fine but the F&B count within the catchment radius fell below the minimum.',
      icon:  AlertTriangle,
      iconCls: 'text-amber-500',
      bgCls:   'bg-amber-50 border-amber-200',
      tooltipCls: 'text-amber-700',
    },
    other_rejection: {
      label: 'Other validation issues',
      sub:   'Validation failed for a reason that does not match the standard categories.',
      icon:  AlertTriangle,
      iconCls: 'text-slate-400',
      bgCls:   'bg-slate-50 border-slate-200',
      tooltipCls: 'text-slate-700',
    },
  };
  const TRUNCATION_CLASS = {
    label: 'Skipped — API budget cap reached',
    sub:   'This candidate was NOT evaluated on merit. The run hit its API cost cap before the candidate could be processed. Re-run with a higher budget to include it.',
    icon:  ShieldCheck,
    iconCls: 'text-sky-500',
    bgCls:   'bg-sky-50 border-sky-200',
    tooltipCls: 'text-sky-700',
  };

  const categorize = (reason) => {
    const r = String(reason || '').toLowerCase();
    if (r.startsWith('geocode_') || r === 'geocoding_failed') return 'geocoding_failed';
    if (r.startsWith('insufficient_fnb') || r.includes('fnb_count')) return 'insufficient_fnb';
    return 'other_rejection';
  };

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of (rejected || [])) {
      const k = categorize(r.reason);
      const list = m.get(k) || [];
      list.push(r);
      m.set(k, list);
    }
    return m;
  }, [rejected]);

  return (
    <div className="space-y-5">
      {/* Validated */}
      <div>
        <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-2">
          <CheckCircle2 className="inline w-3 h-3 mr-1" />
          Validated · {candidates.length}
        </div>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {candidates.map((c, i) => (
            <li key={c.name}
                data-testid={`city-intel-step2-validated-${i}`}
                className="flex items-center justify-between text-sm bg-emerald-50/40 rounded-lg px-3 py-1.5 border border-emerald-100">
              <span className="text-slate-800 truncate">{c.name}</span>
              <span className="text-[11px] text-emerald-700 ml-2 shrink-0 tabular-nums">
                F&B {c.fnb_count}
              </span>
            </li>
          ))}
          {candidates.length === 0 && (
            <li className="text-sm text-slate-500 italic">None.</li>
          )}
        </ul>
      </div>

      {/* Rejections (per category) */}
      {['geocoding_failed', 'insufficient_fnb', 'other_rejection'].map(k => {
        const items = grouped.get(k) || [];
        if (items.length === 0) return null;
        const klass = REJECTION_CLASSES[k];
        const Icon = klass.icon;
        return (
          <div key={k}>
            <div className={`text-xs uppercase tracking-wider font-semibold mb-2 ${klass.tooltipCls}`}>
              <Icon className={`inline w-3 h-3 mr-1 ${klass.iconCls}`} />
              {klass.label} · {items.length}
            </div>
            <div className="text-[12px] text-slate-500 mb-2 max-w-2xl">{klass.sub}</div>
            <ul className="space-y-1.5">
              {items.map((it, i) => (
                <li key={`${it.name}-${i}`}
                    data-testid={`city-intel-step2-rejected-${k}-${i}`}
                    className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 border ${klass.bgCls}`}>
                  <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${klass.iconCls}`} />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-slate-900">{it.name}</span>
                    <span className="text-slate-600"> — {it.reason}</span>
                    {typeof it.fnb_count === 'number' && (
                      <span className="text-slate-500"> (F&B: {it.fnb_count})</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      {/* Truncated — VISUALLY DISTINCT, never confused with quality-rejection */}
      {(truncated || []).length > 0 && (
        <div>
          <div className={`text-xs uppercase tracking-wider font-semibold mb-2 ${TRUNCATION_CLASS.tooltipCls}`}>
            <ShieldCheck className={`inline w-3 h-3 mr-1 ${TRUNCATION_CLASS.iconCls}`} />
            {TRUNCATION_CLASS.label} · {truncated.length}
          </div>
          <div className="text-[12px] text-slate-500 mb-2 max-w-2xl italic">
            {TRUNCATION_CLASS.sub}
          </div>
          <ul className="space-y-1.5">
            {truncated.map((it, i) => (
              <li key={`${it.name}-${i}`}
                  data-testid={`city-intel-step2-truncated-${i}`}
                  className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 border ${TRUNCATION_CLASS.bgCls}`}>
                <ShieldCheck className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${TRUNCATION_CLASS.iconCls}`} />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-slate-900">{it.name}</span>
                  <span className="text-slate-600"> — not evaluated on merit</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ─── Step 3 — demand-gate verdicts per validated candidate ──────── */
function Step3Details({ candidates }) {
  if (!candidates || candidates.length === 0) {
    return <div className="text-sm text-slate-500 italic">No candidates to show.</div>;
  }
  const sorted = [...candidates].sort((a, b) => {
    const order = { PASS: 0, SOFT_PENALTY: 1, FAIL: 2 };
    const av = order[a.demand?.verdict] ?? 3;
    const bv = order[b.demand?.verdict] ?? 3;
    return av - bv;
  });
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
        Demand-gate verdicts · {sorted.length}
      </div>
      <ul className="space-y-2">
        {sorted.map((c, i) => {
          const v = c.demand?.verdict || 'UNKNOWN';
          const pf = c.demand?.penalty_factor;
          const evidence = c.demand?.reasoning
                            || (Array.isArray(c.demand?.evidence) && c.demand.evidence[0])
                            || '—';
          const vCls = v === 'PASS'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : v === 'SOFT_PENALTY'
              ? 'bg-amber-50 text-amber-800 border-amber-200'
              : v === 'FAIL'
                ? 'bg-rose-50 text-rose-700 border-rose-200'
                : 'bg-slate-100 text-slate-700 border-slate-200';
          const vLabel = v === 'PASS' ? 'PASS'
                        : v === 'SOFT_PENALTY' ? 'SOFT'
                        : v === 'FAIL' ? 'FAIL' : v;
          return (
            <li key={`${c.name}-${i}`}
                data-testid={`city-intel-step3-verdict-${i}`}
                className="grid grid-cols-[10rem_auto_1fr] gap-3 items-start text-sm py-2 border-b border-slate-100 last:border-b-0">
              <span className="font-semibold text-slate-900 truncate">{c.name}</span>
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide border ${vCls}`}>
                {vLabel}{typeof pf === 'number' && ` (${pf.toFixed(2)})`}
              </span>
              <span className="text-slate-700">{evidence}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ─── Step 4 — ALL ranked candidates (not just top 4) ────────────── */
function Step4Details({ candidates, onSelectCandidate }) {
  if (!candidates || candidates.length === 0) {
    return <div className="text-sm text-slate-500 italic">No ranked candidates.</div>;
  }
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
        Full ranking · {candidates.length}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left font-semibold py-2 pr-3">#</th>
              <th className="text-left font-semibold py-2 pr-3">Neighbourhood</th>
              <th className="text-right font-semibold py-2 pr-3">Composite</th>
              <th className="text-left font-semibold py-2 pr-3">Verdict</th>
              <th className="text-left font-semibold py-2 pr-3">Opportunity</th>
              <th className="text-right font-semibold py-2"></th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c, i) => {
              const v = c.demand?.verdict || '—';
              const composite = c.scores?.composite ?? c.composite_score ?? 0;
              const oppType = c.recommendation?.opportunity_type || '—';
              const tags    = c.recommendation?.opportunity_tags || [];
              const vCls = v === 'PASS'   ? 'text-emerald-700'
                          : v === 'SOFT_PENALTY' ? 'text-amber-700'
                          : v === 'FAIL'  ? 'text-rose-700' : 'text-slate-500';
              return (
                <tr key={c.name}
                    data-testid={`city-intel-step4-row-${i}`}
                    className="border-b border-slate-100 last:border-b-0 hover:bg-white">
                  <td className="py-2 pr-3 tabular-nums text-slate-900 font-semibold">{c.rank}</td>
                  <td className="py-2 pr-3 text-slate-900 font-medium">{c.name}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-900 font-semibold">
                    {Number(composite).toFixed(2)}
                  </td>
                  <td className={`py-2 pr-3 text-[11px] font-semibold uppercase tracking-wide ${vCls}`}>
                    {v}
                  </td>
                  <td className="py-2 pr-3 text-slate-700 text-[12px]">
                    <span className="font-medium text-slate-900">{oppType}</span>
                    {tags.length > 0 && (
                      <span className="text-slate-500"> · {tags.join(' · ')}</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onSelectCandidate && onSelectCandidate(c)}
                      data-testid={`city-intel-step4-view-${i}`}
                      className="text-[12px] font-medium text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline">
                      View details →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SCREEN 4 — Opportunity Explorer (premium recommendation cards)
   ════════════════════════════════════════════════════════════════════ */
function OpportunityExplorerScreen({ result, onSelect, onBack, onAnalyse }) {
  const candidates = result?.candidates || [];
  return (
    <div className="space-y-6" data-testid="city-intel-explorer-screen">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-medium text-slate-900">
            Top opportunities in {result?.city}
          </h2>
          <p className="text-base text-slate-600 mt-2 max-w-2xl">
            Each candidate has been validated against live evidence and
            scored on demand match, crowd pull and saturation.
            Click any card to dive into the full intelligence.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <GenerateReportButton result={result} brief={result?.brief} />
          <Button variant="outline" onClick={onBack}
                  data-testid="city-intel-explorer-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Decision journey
          </Button>
        </div>
      </div>

      {candidates.length === 0 && (
        <Card><CardContent className="p-10 text-center text-slate-500">
          The engine returned no validated candidates for this concept.
          Try broadening the target segments or relaxing the city.
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5"
           data-testid="city-intel-explorer-grid">
        {candidates.map(c => (
          <CandidateCard key={c.name}
                         candidate={c}
                         onSelect={() => onSelect(c)}
                         onAnalyse={() => onAnalyse(c)} />
        ))}
      </div>
    </div>
  );
}

function ScoreDial({ label, value, tone = 'slate' }) {
  // Tone classes — explicit Tailwind tokens so the JIT keeps them.
  const toneCls = {
    slate:   'bg-slate-100 text-slate-700',
    ok:      'bg-emerald-50  text-emerald-700',
    warn:    'bg-amber-50    text-amber-800',
    err:     'bg-rose-50     text-rose-700',
  }[tone] || 'bg-slate-100 text-slate-700';
  return (
    <div className="flex-1 rounded-xl border border-slate-100 px-4 py-3 bg-white">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-2xl font-semibold text-slate-900 tabular-nums">
          {Math.round(value)}
        </span>
        <span className="text-xs text-slate-400">/100</span>
        <span className={`ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${toneCls}`}>
          {tone === 'ok'   ? 'STRONG'
          : tone === 'warn' ? 'WATCH'
          : tone === 'err'  ? 'WEAK'  : '—'}
        </span>
      </div>
    </div>
  );
}

function dialTone(value) {
  if (value >= 70) return 'ok';
  if (value >= 40) return 'warn';
  return 'err';
}

function VerdictBadge({ verdict }) {
  const map = {
    PASS:         { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', l: 'Demand match' },
    SOFT_PENALTY: { cls: 'bg-amber-50  text-amber-800  border-amber-200',  l: 'Partial match' },
    FAIL:         { cls: 'bg-rose-50    text-rose-700    border-rose-200',    l: 'Demand mismatch' },
  };
  const m = map[verdict] || { cls: 'bg-slate-100 text-slate-700 border-slate-200', l: 'Unknown' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
      text-[11px] font-semibold uppercase tracking-wide border ${m.cls}`}>
      <ShieldCheck className="w-3 h-3" /> {m.l}
    </span>
  );
}

function RiskBadge({ risk }) {
  const map = {
    LOW:    'bg-emerald-50 text-emerald-700 border-emerald-200',
    MEDIUM: 'bg-amber-50  text-amber-800  border-amber-200',
    HIGH:   'bg-rose-50    text-rose-700    border-rose-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
      text-[11px] font-semibold uppercase tracking-wide border
      ${map[risk] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
      Risk · {risk || '—'}
    </span>
  );
}

function CandidateCard({ candidate, onSelect, onAnalyse }) {
  const c = candidate;
  const rec = c.recommendation || {};
  const conf = Math.round(((c.demand?.confidence) || 0) * 100);
  return (
    <Card
      data-testid={`city-intel-candidate-${(c.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
      onClick={onSelect}
    >
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-full bg-slate-900 text-amber-300
                          flex items-center justify-center text-base font-semibold">
            #{c.rank}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-slate-900 leading-tight">
              {c.name}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[12px] font-medium
                                text-slate-600 px-2 py-0.5 bg-slate-100 rounded-full">
                <Sparkle className="w-3 h-3" /> {rec.opportunity_type || 'Catchment'}
              </span>
              <VerdictBadge verdict={c.demand?.verdict} />
              <RiskBadge risk={rec.risk} />
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Composite</div>
            <div className="text-3xl font-semibold tabular-nums text-slate-900">
              {Math.round(c.composite_score)}
            </div>
          </div>
        </div>

        {/* Score dials */}
        <div className="flex flex-wrap gap-2">
          <ScoreDial label="Demand match"
                     value={c.scores?.demand_match || 0}
                     tone={dialTone(c.scores?.demand_match || 0)} />
          <ScoreDial label="Crowd pull"
                     value={c.scores?.crowd_pull || 0}
                     tone={dialTone(c.scores?.crowd_pull || 0)} />
          <ScoreDial label="Saturation"
                     value={c.scores?.saturation || 0}
                     tone={dialTone(c.scores?.saturation || 0)} />
        </div>

        {/* Strengths vs Risks */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-emerald-50/60 p-3 border border-emerald-100">
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1.5">
              <TrendingUp className="inline w-3 h-3 mr-1" /> Why recommended
            </div>
            <ul className="text-sm text-emerald-900 space-y-1">
              {(rec.why_recommended || []).slice(0, 3).map((p, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
              {(!rec.why_recommended || rec.why_recommended.length === 0) && (
                <li className="text-emerald-700/60 italic">No strong positives</li>
              )}
            </ul>
          </div>
          <div className="rounded-xl bg-rose-50/40 p-3 border border-rose-100">
            <div className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold mb-1.5">
              <TrendingDown className="inline w-3 h-3 mr-1" /> Key risks
            </div>
            <ul className="text-sm text-rose-900 space-y-1">
              {(rec.why_not || []).slice(0, 3).map((p, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
              {(!rec.why_not || rec.why_not.length === 0) && (
                <li className="text-rose-700/60 italic">No material risks flagged</li>
              )}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 pt-1 flex-wrap">
          <span className="text-xs text-slate-500">
            <span className="font-medium text-slate-700">{c.fnb_count}</span> F&B nearby
            &nbsp;·&nbsp;
            Demand confidence <span className="font-medium text-slate-700">{conf}%</span>
          </span>
          <div className="ml-auto flex items-center gap-2"
               onClick={e => e.stopPropagation()}>
            <Button variant="outline" size="sm"
                    onClick={onSelect}
                    data-testid={`city-intel-candidate-details-${(c.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              View details
            </Button>
            <Button size="sm"
                    onClick={onAnalyse}
                    className="bg-slate-900 hover:bg-slate-800 text-amber-300"
                    data-testid={`city-intel-candidate-analyse-${(c.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              Analyse this area <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SCREEN 5 — Neighbourhood Detail
   ════════════════════════════════════════════════════════════════════ */
const DIMENSION_LABELS = {
  student_density:       'Student',
  professional_density:  'Professional',
  family_presence:       'Family',
  affluence:             'Affluence',
  corporate_activity:    'Corporate',
  nightlife_activity:    'Nightlife',
  tourist_activity:      'Tourist',
  health_wellness:       'Wellness',
};

/* ──────────────────────────────────────────────────────────────────
   ITER268 — Per-candidate detail sub-components (the "show your
   work" surface). All data sourced from the existing API response;
   no backend changes.
   ────────────────────────────────────────────────────────────────── */

/** POI counts grouped by demand dimension, with the run totals. */
function DemandPOIBreakdown({ demandProfile, evidence }) {
  const dp = demandProfile || {};
  const byDim = useMemo(() => {
    const m = new Map();
    for (const e of (evidence || [])) {
      const dim = e.dimension || 'unknown';
      const list = m.get(dim) || [];
      list.push(e);
      m.set(dim, list);
    }
    return m;
  }, [evidence]);

  const dimsWithScore = Object.keys(DIMENSION_LABELS)
    .map(k => ({ key: k, label: DIMENSION_LABELS[k], score: Number(dp[k] || 0),
                 pois: byDim.get(k) || [] }))
    .filter(d => d.score > 0 || d.pois.length > 0)
    .sort((a, b) => b.score - a.score);

  if (dimsWithScore.length === 0) {
    return null;
  }
  return (
    <Card data-testid="city-intel-detail-poi-breakdown">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Demand evidence by dimension
          </div>
          <div className="text-[11px] text-slate-400">
            Total signal hits: {dp.total_evidence_hits ?? evidence.length}
          </div>
        </div>
        <div className="space-y-3">
          {dimsWithScore.map(d => (
            <div key={d.key}
                 data-testid={`city-intel-dim-${d.key}`}
                 className="rounded-xl border border-slate-100 bg-slate-50/40 px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-900">{d.label}</span>
                <span className="text-sm tabular-nums text-slate-700">
                  <span className={d.score >= 60 ? 'text-emerald-700 font-semibold'
                                  : d.score >= 20 ? 'text-amber-700' : 'text-slate-500'}>
                    {d.score}
                  </span>
                  <span className="text-slate-400">/100</span>
                </span>
              </div>
              {d.pois.length === 0 ? (
                <div className="text-[12px] text-slate-400 italic">
                  Score derived from cross-dimension signal — no direct POI evidence collected.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {d.pois.map((p, i) => (
                    <span key={i}
                          className="inline-flex items-center gap-1 text-[12px] bg-white border border-slate-200 rounded-lg px-2 py-1 text-slate-700">
                      <span className="font-medium text-slate-900">{p.type}</span>
                      <span className="text-slate-400">×</span>
                      <span className="tabular-nums text-slate-700">{p.count}</span>
                      <span className="text-[10px] text-slate-400 uppercase ml-1">{p.source}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** Saturation card — ratio, modifier, threshold band verdict. */
function SaturationCard({ candidate }) {
  const c = candidate;
  const ratio = c.saturation_ratio ?? 0;
  const mod = c.saturation_modifier ?? 1.0;
  const fnb = c.fnb_count ?? 0;
  const conceptMatch = c.concept_match_count ?? 0;
  // Band classification mirrors the backend ITER267 logic.
  let band, bandCls, bandSub;
  if (ratio < 0.10) {
    if (fnb < 12) {
      band = 'Underproven';
      bandCls = 'bg-amber-50 text-amber-800 border-amber-200';
      bandSub = 'Whitespace, but the sample is too small to know if demand is real (fnb<12).';
    } else {
      band = 'Whitespace';
      bandCls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
      bandSub = 'Healthy F&B base with very few same-concept competitors — green-field opportunity.';
    }
  } else if (ratio <= 0.35) {
    band = 'Healthy';
    bandCls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    bandSub = 'Proven concept fit — enough peers to validate demand, not enough to saturate.';
  } else if (ratio <= 0.60) {
    band = 'Crowding';
    bandCls = 'bg-amber-50 text-amber-800 border-amber-200';
    bandSub = 'Concept density is rising — differentiation matters more here.';
  } else {
    band = 'Oversaturated';
    bandCls = 'bg-rose-50 text-rose-700 border-rose-200';
    bandSub = 'Concept is over-represented — entry requires a clear edge or alternate format.';
  }
  return (
    <Card data-testid="city-intel-detail-saturation">
      <CardContent className="p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Saturation
        </div>
        <div className="text-3xl font-semibold text-slate-900 tabular-nums">
          {Math.round(ratio * 100)}%
        </div>
        <div className="text-xs text-slate-500">
          {conceptMatch} similar concepts ÷ {fnb} F&B operators = {ratio.toFixed(3)} ratio
        </div>
        <div className="pt-1">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide border ${bandCls}`}>
            {band}
          </span>
          <div className="text-xs text-slate-600 mt-2 leading-relaxed">{bandSub}</div>
        </div>
        <div className="pt-1 text-xs text-slate-500 border-t border-slate-100">
          Saturation modifier <span className="tabular-nums font-semibold text-slate-700">×{mod.toFixed(2)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

/** Crowd-pull card — aggregate signal only, no individual venues. */
function CrowdPullCard({ candidate }) {
  const c = candidate;
  const cp = c.scores?.crowd_pull ?? c.crowd_pull ?? 0;
  const fnb = c.fnb_count ?? 0;
  const conceptMatch = c.concept_match_count ?? 0;
  return (
    <Card data-testid="city-intel-detail-crowd-pull">
      <CardContent className="p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Crowd-pull (quality × volume)
        </div>
        <div className="text-3xl font-semibold text-slate-900 tabular-nums">
          {Number(cp).toFixed(2)}
          <span className="text-base text-slate-400 font-normal">/100</span>
        </div>
        <div className="text-xs text-slate-600 leading-relaxed">
          Aggregate quality-weighted footfall signal across the {fnb} F&B venues found in the
          catchment, plus the anchor-density bonus. Quality = rating × log(reviews+1).
        </div>
        <div className="pt-1 text-xs text-slate-500 border-t border-slate-100 space-y-1">
          <div>F&B operators in catchment: <span className="tabular-nums font-semibold text-slate-700">{fnb}</span></div>
          <div>Same-concept competitors: <span className="tabular-nums font-semibold text-slate-700">{conceptMatch}</span></div>
        </div>
        <div className="pt-1 text-[11px] text-slate-400 italic">
          Aggregate signal only — individual venue ratings and reviews are not
          surfaced here. Drill into Site Intelligence for venue-level competitor analysis.
        </div>
      </CardContent>
    </Card>
  );
}

/** Composite math card — show the formula and numbers, no narrative. */
function CompositeMathCard({ candidate }) {
  const c = candidate;
  const cp = c.scores?.crowd_pull ?? c.crowd_pull ?? 0;
  const pf = c.demand?.penalty_factor ?? 1.0;
  const mod = c.saturation_modifier ?? 1.0;
  const composite = c.scores?.composite ?? c.composite_score ?? 0;
  const product = cp * pf * mod;
  const drift = Math.abs(product - composite);
  return (
    <Card data-testid="city-intel-detail-composite-math">
      <CardContent className="p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Composite math
        </div>
        <div className="text-3xl font-semibold text-slate-900 tabular-nums">
          {Number(composite).toFixed(2)}
        </div>
        <div className="text-xs text-slate-600 font-mono leading-relaxed bg-slate-50/60 rounded-lg px-3 py-2 border border-slate-100">
          composite = crowd_pull × penalty_factor × saturation_modifier
        </div>
        <div className="text-xs text-slate-700 font-mono leading-relaxed pl-1 space-y-1">
          <div>= <span className="tabular-nums text-slate-900 font-semibold">{Number(cp).toFixed(2)}</span> × <span className="tabular-nums text-slate-900 font-semibold">{pf.toFixed(2)}</span> × <span className="tabular-nums text-slate-900 font-semibold">{mod.toFixed(2)}</span></div>
          <div>= <span className="tabular-nums text-slate-900 font-semibold">{product.toFixed(2)}</span></div>
        </div>
        {drift > 0.5 && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
            Rounding/precision: reported composite {composite.toFixed(2)} differs from
            recomputed {product.toFixed(2)} by {drift.toFixed(2)}. Engine may apply
            additional weighting not yet exposed.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Opportunity tag rationale — explain WHY this got "Premium Destination"
    vs "Corporate Cluster" etc., based on the top-scoring demand dim. */
function OpportunityRationaleCard({ candidate }) {
  const c = candidate;
  const rec = c.recommendation || {};
  const dp = c.demand_profile || {};
  // Find the top dim and its margin over the runner-up — same logic the
  // backend ITER267 rule uses to pick the tag.
  const dims = Object.keys(DIMENSION_LABELS)
    .map(k => ({ key: k, label: DIMENSION_LABELS[k], score: Number(dp[k] || 0) }))
    .filter(d => d.score > 0)
    .sort((a, b) => b.score - a.score);
  const top1 = dims[0];
  const top2 = dims[1];
  const tags = rec.opportunity_tags || [];
  return (
    <Card data-testid="city-intel-detail-opportunity-rationale">
      <CardContent className="p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Opportunity tag rationale
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-semibold text-slate-900">
            {rec.opportunity_type || 'Catchment'}
          </span>
          {tags.map(t => (
            <span key={t} className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-slate-200 text-slate-600">
              {t}
            </span>
          ))}
        </div>
        {top1 && (
          <div className="text-sm text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">{top1.label}</span> dominates
            at <span className="tabular-nums font-semibold">{top1.score}/100</span>
            {top2 && (
              <>
                , {(top1.score - top2.score) >= 15
                    ? `clearly above the runner-up `
                    : `marginally above `}
                <span className="font-semibold text-slate-900">{top2.label}</span> ({top2.score}/100).
                {' '}
                {(top1.score - top2.score) >= 15
                  ? `Margin ≥ 15pts triggers a single-dimension tag.`
                  : `Margin < 15pts — engine applies a "Mixed Catchment" overlay.`}
              </>
            )}
          </div>
        )}
        {(c.demand?.satisfied || []).length > 0 && (
          <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
            Target segments satisfied: <span className="text-slate-800 font-medium">
              {(c.demand.satisfied || []).join(', ')}
            </span>
            {(c.demand?.unsatisfied || []).length > 0 && (
              <> · Unsatisfied: <span className="text-rose-700 font-medium">
                {c.demand.unsatisfied.join(', ')}
              </span></>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Why-this-vs-peer comparator. Defaults to next candidate by rank. */
function CandidateComparator({ candidate, allCandidates }) {
  const c = candidate;
  // Build peer dropdown: every OTHER candidate, sorted by rank.
  const peers = (allCandidates || [])
    .filter(p => p.name !== c.name)
    .sort((a, b) => (a.rank || 999) - (b.rank || 999));
  // Default peer: adjacent rank — prefer rank+1, fallback rank-1.
  const defaultPeer = useMemo(() => {
    const above = peers.find(p => p.rank === (c.rank || 0) + 1);
    const below = peers.find(p => p.rank === (c.rank || 0) - 1);
    return above || below || peers[0] || null;
  }, [peers, c.rank]);
  const [peerName, setPeerName] = useState(defaultPeer?.name || '');
  const peer = peers.find(p => p.name === peerName) || defaultPeer;
  if (!peer) return null;

  const compositeA = c.scores?.composite ?? c.composite_score ?? 0;
  const compositeB = peer.scores?.composite ?? peer.composite_score ?? 0;

  // Rows to compare. Each row defines a label + a getter that returns a
  // number (or null if missing). Highlights triggered when |delta| >=
  // its `heavy` threshold.
  const rows = [
    { label: 'composite_score',     get: x => x.scores?.composite ?? x.composite_score, heavy: 0.5, fmt: v => Number(v).toFixed(2) },
    { label: 'crowd_pull_score',    get: x => x.scores?.crowd_pull ?? x.crowd_pull,     heavy: 0.5, fmt: v => Number(v).toFixed(2) },
    { label: 'demand_match',        get: x => x.scores?.demand_match,                    heavy: 5,   fmt: v => Number(v).toFixed(0) },
    { label: 'saturation_modifier', get: x => x.saturation_modifier,                     heavy: 0.05,fmt: v => Number(v).toFixed(2) },
    { label: 'saturation_ratio',    get: x => x.saturation_ratio,                        heavy: 0.05,fmt: v => Number(v).toFixed(3) },
    { label: 'total_fnb',           get: x => x.fnb_count,                                heavy: 10,  fmt: v => String(v) },
    { label: 'similar_concepts',    get: x => x.concept_match_count,                      heavy: 5,   fmt: v => String(v) },
    ...Object.keys(DIMENSION_LABELS)
      // D3 — unmeasured dims never appear in comparisons (no phantom
      // "0 vs 0 tied" rows).
      .filter(k => {
        const probed = new Set([
          ...(c.demand_profile?.probed_dimensions || []),
          ...(peer.demand_profile?.probed_dimensions || []),
        ]);
        return probed.size === 0 || probed.has(k);
      })
      .map(k => ({
        label: `dim.${k}`,
        get: x => x.demand_profile?.[k],
        heavy: 10,
        fmt: v => Number(v).toFixed(0),
      })),
  ];

  return (
    <Card data-testid="city-intel-detail-comparator">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Why this is #{c.rank} vs #{peer.rank}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Pick any candidate to defend the rank gap. Differences highlighted in amber.
            </div>
          </div>
          <select
            value={peer.name}
            onChange={e => setPeerName(e.target.value)}
            data-testid="city-intel-comparator-peer-select"
            className="text-sm rounded-lg border border-slate-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300">
            {peers.map(p => (
              <option key={p.name} value={p.name}>
                #{p.rank} — {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-100">
                <th className="text-left font-semibold py-2 px-3"> </th>
                <th className="text-right font-semibold py-2 px-3 text-slate-700">
                  {c.name} <span className="text-slate-400">(#{c.rank})</span>
                </th>
                <th className="text-right font-semibold py-2 px-3 text-slate-700">
                  {peer.name} <span className="text-slate-400">(#{peer.rank})</span>
                </th>
                <th className="text-right font-semibold py-2 px-3">Δ</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100 bg-amber-50/40">
                <td className="py-2 px-3 font-mono text-xs text-slate-600">composite</td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold text-slate-900">{Number(compositeA).toFixed(2)}</td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold text-slate-900">{Number(compositeB).toFixed(2)}</td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold">
                  {(compositeA - compositeB) > 0
                    ? <span className="text-emerald-700">+{(compositeA - compositeB).toFixed(2)}</span>
                    : (compositeA - compositeB) < 0
                      ? <span className="text-rose-700">{(compositeA - compositeB).toFixed(2)}</span>
                      : <span className="text-slate-500">tied</span>}
                </td>
              </tr>
              {rows.filter(r => r.label !== 'composite_score').map(r => {
                const va = r.get(c);
                const vb = r.get(peer);
                if (va == null && vb == null) return null;
                const num_a = Number(va || 0);
                const num_b = Number(vb || 0);
                const delta = num_a - num_b;
                const heavy = Math.abs(delta) >= r.heavy;
                return (
                  <tr key={r.label}
                      data-testid={`city-intel-comparator-row-${r.label}`}
                      className={`border-b border-slate-100 last:border-b-0 ${heavy ? 'bg-amber-50/30' : ''}`}>
                    <td className="py-2 px-3 font-mono text-xs text-slate-600">{r.label}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-slate-800">
                      {va != null ? r.fmt(va) : '—'}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-slate-800">
                      {vb != null ? r.fmt(vb) : '—'}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-sm">
                      {Math.abs(delta) < 1e-9 ? (
                        <span className="text-slate-400">tied</span>
                      ) : (
                        <span className={`${heavy ? 'font-semibold' : ''} ${delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {delta > 0 ? '+' : ''}{r.fmt(delta)}
                          {heavy && ' ←'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function NeighbourhoodDetailScreen({ candidate, city, brief, onBack, onAnalyse, allCandidates }) {
  const c = candidate;
  const rec = c.recommendation || {};
  const dp = c.demand_profile || {};
  const conf = Math.round(((c.demand?.confidence) || 0) * 100);
  const llmConf = Math.round(((c.confidence) || 0) * 100);

  // D3 — unmeasured dims are omitted, never rendered as 0/100.
  const probedSet = new Set(dp.probed_dimensions || []);
  const dimEntries = Object.keys(DIMENSION_LABELS)
    .filter(k => probedSet.size === 0 || probedSet.has(k))
    .map(k => ({
      key: k, label: DIMENSION_LABELS[k], value: Number(dp[k] || 0),
    })).sort((a, b) => b.value - a.value);

  const briefLabel = [
    (brief?.preferred_cuisines || []).slice(0, 3).map(x => x.replace(/_/g, ' ')).join(', '),
    (brief?.tier || '').replace(/_/g, ' '),
    ((brief?.preferred_formats || [])[0] || '').replace(/_/g, ' '),
  ].filter(Boolean).join(' · ');

  // Static Maps URL — uses the backend-provided lat/lng. No API key
  // required for the embed; falls back gracefully if env not set.
  const mapsSrc =
    `https://www.google.com/maps?q=${c.lat},${c.lng}&z=14&output=embed`;

  return (
    <div className="space-y-6" data-testid="city-intel-detail-screen">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="outline" size="sm" onClick={onBack}
                data-testid="city-intel-detail-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <MapPin className="w-3.5 h-3.5" /> {city}
            &nbsp;·&nbsp; Ranked #{c.rank}
          </div>
          <h2 className="text-3xl font-semibold text-slate-900 mt-1">{c.name}</h2>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1 text-[12px] font-medium
                              text-slate-700 px-2.5 py-1 bg-slate-100 rounded-full">
              <Sparkle className="w-3 h-3" /> {rec.opportunity_type || 'Catchment'}
            </span>
            {(rec.opportunity_tags || []).map(t => (
              <span key={t} className="text-[11px] uppercase tracking-wider px-2 py-0.5
                                       rounded-full border border-slate-200 text-slate-600">
                {t}
              </span>
            ))}
            <VerdictBadge verdict={c.demand?.verdict} />
            <RiskBadge risk={rec.risk} />
          </div>
        </div>
        <Button size="lg" onClick={onAnalyse}
                data-testid="city-intel-detail-run-site-btn"
                className="bg-slate-900 hover:bg-slate-800 text-amber-300 px-6 h-12 rounded-full">
          Run Site Intelligence <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Executive summary */}
      {rec.executive_summary && (
        <Card>
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Executive summary
            </div>
            <p className="text-base text-slate-800 leading-relaxed">
              {rec.executive_summary}
            </p>
            {rec.provenance?.deterministic && (
              <div className="mt-3 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                Deterministic — assembled from rules, no second LLM call.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Map + score breakdown */}
        <Card>
          <CardContent className="p-0 overflow-hidden">
            <div className="aspect-[4/3] w-full">
              <iframe
                title={`${c.name} map`}
                src={mapsSrc}
                loading="lazy"
                width="100%" height="100%" style={{ border: 0 }}
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="city-intel-detail-map"
              />
            </div>
            <div className="p-5 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
                Score breakdown
              </div>
              <div className="grid grid-cols-2 gap-2">
                <ScoreDial label="Composite" value={c.scores?.composite || c.composite_score || 0}
                           tone={dialTone(c.scores?.composite || c.composite_score || 0)} />
                <ScoreDial label="Demand match" value={c.scores?.demand_match || 0}
                           tone={dialTone(c.scores?.demand_match || 0)} />
                <ScoreDial label="Crowd pull" value={c.scores?.crowd_pull || 0}
                           tone={dialTone(c.scores?.crowd_pull || 0)} />
                <ScoreDial label="Saturation modifier" value={c.scores?.saturation || 0}
                           tone={dialTone(c.scores?.saturation || 0)} />
              </div>
              <div className="mt-4 text-xs text-slate-500">
                <span className="font-medium text-slate-700">{c.fnb_count}</span> F&B operators within 1.5 km
                &nbsp;·&nbsp;
                Saturation ratio <span className="font-medium text-slate-700">
                  {Math.round(((c.saturation_ratio || 0)) * 100)}%
                </span>
                &nbsp;·&nbsp;
                Concept-match competitors <span className="font-medium text-slate-700">
                  {c.concept_match_count || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Demand profile */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Demand profile
            </div>
            <div className="space-y-2.5">
              {dimEntries.map(({ key, label, value }) => (
                <div key={key}>
                  <div className="flex items-center justify-between text-sm">
                    <span className={value >= 60 ? 'text-slate-900 font-medium' : 'text-slate-700'}>
                      {label}
                    </span>
                    <span className="tabular-nums text-slate-500">{value}/100</span>
                  </div>
                  <div className="mt-1 w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${value >= 60 ? 'bg-emerald-500'
                                         : value >= 20 ? 'bg-amber-400'
                                         : 'bg-slate-300'}`}
                      style={{ width: `${value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {c.demand?.reasoning && (
              <div className="pt-2 text-sm text-slate-600 italic border-t border-slate-100">
                {c.demand.reasoning}
              </div>
            )}
            <div className="pt-1 text-xs text-slate-500">
              Demand confidence <span className="font-medium text-slate-700">{conf}%</span>
              &nbsp;·&nbsp;
              LLM proposal confidence <span className="font-medium text-slate-700">{llmConf}%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Evidence + why ranked */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Evidence collected
            </div>
            {(c.demand?.evidence_structured || []).length === 0 && (
              <p className="text-sm text-slate-500">No POI evidence — target_segments unset or zero hits.</p>
            )}
            <ul className="grid grid-cols-2 gap-1.5">
              {(c.demand?.evidence_structured || []).map((e, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-slate-50/60 rounded-lg px-3 py-1.5">
                  <span className="text-slate-700 truncate">{e.type}</span>
                  <span className="tabular-nums text-slate-500 ml-2">×{e.count}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Why ranked here
            </div>
            <ul className="space-y-1.5">
              {(c.why_ranked_here || []).map((w, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="w-1.5 h-1.5 mt-2 rounded-full bg-slate-400 shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Best-suited + avoid concepts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-3">
              Best-suited concepts
            </div>
            <div className="flex flex-wrap gap-2">
              {(rec.best_concepts || []).map((b, i) => (
                <span key={i}
                      data-testid={`city-intel-detail-best-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                 text-sm bg-emerald-50 text-emerald-800 border border-emerald-200">
                  <ChefHat className="w-3.5 h-3.5" /> {b}
                </span>
              ))}
              {(rec.best_concepts || []).length === 0 && (
                <span className="text-sm text-slate-500">—</span>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold mb-3">
              Concepts to avoid
            </div>
            <div className="flex flex-wrap gap-2">
              {(rec.concepts_to_avoid || []).map((b, i) => (
                <span key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                 text-sm bg-rose-50 text-rose-800 border border-rose-200">
                  <XCircle className="w-3.5 h-3.5" /> {b}
                </span>
              ))}
              {(rec.concepts_to_avoid || []).length === 0 && (
                <span className="text-sm text-slate-500">—</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendation summary */}
      {(rec.why_recommended?.length || rec.why_not?.length) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card>
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-3">
                <TrendingUp className="inline w-3 h-3 mr-1" /> Why recommended
              </div>
              <ul className="space-y-1.5">
                {(rec.why_recommended || []).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-emerald-900">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold mb-3">
                <TrendingDown className="inline w-3 h-3 mr-1" /> Why not (risks)
              </div>
              <ul className="space-y-1.5">
                {(rec.why_not || []).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-rose-900">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-600 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
                {(!rec.why_not || rec.why_not.length === 0) && (
                  <li className="text-sm text-slate-500 italic">No material risks flagged.</li>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* ITER268 — Demand POI breakdown by dimension */}
      <DemandPOIBreakdown demandProfile={dp} evidence={c.demand?.evidence_structured || []} />

      {/* ITER268 — Saturation + Crowd-pull + Composite math */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <SaturationCard candidate={c} />
        <CrowdPullCard candidate={c} />
        <CompositeMathCard candidate={c} />
      </div>

      {/* ITER268 — Opportunity tag rationale */}
      <OpportunityRationaleCard candidate={c} />

      {/* ITER268 — Why-this-vs-that comparator */}
      {Array.isArray(allCandidates) && allCandidates.length > 1 && (
        <CandidateComparator candidate={c} allCandidates={allCandidates} />
      )}

      {/* Footer hand-off */}
      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-slate-500 max-w-md">
          Selecting <em>Run Site Intelligence</em> hands this neighbourhood
          to the deep site analysis — geocoded address, competitor matrix,
          Market DNA, financial viability and recommendation.
        </div>
        <Button size="lg" onClick={onAnalyse}
                data-testid="city-intel-detail-run-site-btn-bottom"
                className="bg-slate-900 hover:bg-slate-800 text-amber-300 px-7 h-12 rounded-full">
          Run Site Intelligence <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   PARENT — CityIntelligence page
   ════════════════════════════════════════════════════════════════════ */
const EMPTY_FORM = {
  business_objective: '',
  preferred_cuisines: [],
  preferred_formats:  [],
  price_for_two:      '',
  city:               '',
  target_segments:    [],
  target_audience_description: '',
  // 4-Axis Addendum — new brief fields
  tier:               '',    // single-select: premium | mid_market | budget_qsr
  format:             '',    // single-select: from format_taxonomy
  dietary:            [],    // multi-select: pure_veg | vegan | jain | halal | non_veg | healthy
  // Client-specified preferred areas — per-run only, NEVER saved to
  // the tenant brief. Max 8 chips enforced client-side.
  preferred_areas:    [],
};

const PREFERRED_AREAS_MAX = 4;

export default function CityIntelligence() {
  const { getHeaders } = useAuth();
  const headers = useMemo(() => getHeaders(), [getHeaders]);
  const navigate = useNavigate();

  const [step, setStep] = useState('concept');
  const stepIndex = STEPS.findIndex(s => s.key === step);

  const [form, setForm] = useState(EMPTY_FORM);
  const [savedBrief, setSavedBrief] = useState(null);

  // 4-Axis Addendum — picker taxonomy fetched from backend (DB-sourced,
  // not hardcoded). See routes/ei_taxonomy.py.
  const [taxonomy, setTaxonomy] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/ei/picker/taxonomy`)
      .then(r => { if (!cancelled) setTaxonomy(r.data); })
      .catch(() => { if (!cancelled) setTaxonomy({
        cuisines: [], formats: [], tiers: [], dietary: [],
      }); });
    return () => { cancelled = true; };
  }, []);

  const [seedResult, setSeedResult] = useState(null);
  const [seedError,  setSeedError]  = useState(null);
  const [seedStartedAt, setSeedStartedAt] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // ITER264 — On mount, hydrate from the Expansion Session if the user
  // is returning from Site Intelligence (or simply re-opening the tab).
  // The session carries the brief, the previous seed result and the
  // last-selected candidate so the user lands exactly where they left
  // off. If no session exists we fall through to the legacy saved-brief
  // load below.
  useEffect(() => {
    const s = getSession();
    if (s && s.origin === 'city_intel' && s.brief) {
      setForm(f => ({
        ...f,
        business_objective: s.brief.business_objective || '',
        preferred_cuisines: s.brief.preferred_cuisines  || [],
        preferred_formats:  s.brief.preferred_formats   || [],
        price_for_two:      s.brief.price_for_two ?? '',
        city:               s.city || '',
        target_segments:    s.brief.target_segments     || [],
        target_audience_description: s.brief.target_audience_description || '',
        // 4-Axis Addendum
        tier:               s.brief.tier                || '',
        format:             s.brief.format              || '',
        dietary:            s.brief.dietary             || [],
      }));
      if (Array.isArray(s.candidates) && s.candidates.length > 0) {
        setSeedResult({
          ok:        true,
          city:      s.city,
          candidates: s.candidates,
          rejected:   s.rejected   || [],
          truncated:  s.truncated  || [],
          meta:       s.seed_meta  || {},
          llm:        s.seed_llm   || {},
        });
        if (s.selected_neighbourhood) {
          setSelectedCandidate(s.selected_neighbourhood);
        }
        const resumeStage = s.current_stage || 'explorer';
        // Don't resume into transient 'discovering' state.
        setStep(resumeStage === 'discovering' ? 'explorer' : resumeStage);
      }
    }
  }, []);

  // Fall back to the tenant's saved brief — never overwrites a session
  // hydration above (only fills the fields that the session left empty).
  useEffect(() => {
    if (!API) return;
    axios.get(`${API}/api/expansion-intelligence/brief`, { headers })
      .then(r => {
        const b = r.data?.data;
        if (b) {
          setSavedBrief(b);
          setForm(f => ({
            ...f,
            business_objective: f.business_objective || b.business_objective || '',
            preferred_cuisines: f.preferred_cuisines.length ? f.preferred_cuisines : (b.preferred_cuisines || []),
            preferred_formats:  f.preferred_formats.length  ? f.preferred_formats  : (b.preferred_formats  || []),
            price_for_two:      f.price_for_two || (b.price_for_two ?? ''),
            target_segments:    f.target_segments.length    ? f.target_segments    : (b.target_segments    || []),
            target_audience_description: f.target_audience_description
                                          || b.target_audience_description || '',
          }));
        }
      })
      .catch(() => { /* ignore — brief is optional */ });
  }, []);

  // ITER264 — persist on every meaningful state change so a hand-off
  // (or a refresh) loses nothing.
  useEffect(() => {
    if (!seedResult) return;
    mergeSession({
      origin:      'city_intel',
      brief: {
        business_objective:          form.business_objective,
        preferred_cuisines:          form.preferred_cuisines,
        preferred_formats:           form.preferred_formats,
        price_for_two:               form.price_for_two,
        target_segments:             form.target_segments,
        target_audience_description: form.target_audience_description,
        // 4-Axis Addendum
        tier:                        form.tier,
        format:                      form.format,
        dietary:                     form.dietary,
      },
      city:                  form.city,
      candidates:            seedResult.candidates || [],
      rejected:              seedResult.rejected   || [],
      truncated:             seedResult.truncated  || [],
      seed_meta:             seedResult.meta       || {},
      seed_llm:              seedResult.llm        || {},
      selected_neighbourhood: selectedCandidate || null,
      current_stage:         step,
    });
  }, [step, seedResult, selectedCandidate, form]);

  // ITER269 — async job state. `activeJobId` is the in-flight job we're
  // polling; persisted to sessionStorage so a tab close/reload mid-poll
  // resumes seamlessly without re-firing the seed request.
  const [activeJobId, setActiveJobId] = useState(null);
  const [currentStage, setCurrentStage] = useState(null);
  const pollAbortRef = useRef({ stop: false });

  const ACTIVE_JOB_KEY = 'expansion_session_active_job';

  // ITER269 — Polling client. Resilient to transient network failures
  // (one failed poll backs off and retries, the job is NOT failed).
  // Hard stop after MAX_POLL_MS to prevent runaway polling.
  const MAX_POLL_MS = 5 * 60 * 1000;       // 5 minutes
  const POLL_INTERVAL_MS = 2500;            // 2.5s

  const pollJob = async (jobId, startedAt) => {
    pollAbortRef.current.stop = false;
    let consecutiveFailures = 0;
    while (!pollAbortRef.current.stop) {
      if ((Date.now() - startedAt) > MAX_POLL_MS) {
        setSeedError('The analysis is taking unusually long. The backend ' +
                      'is still running — refresh in a minute to resume.');
        return;
      }
      try {
        // Single-poll timeout — generous enough (30s) to survive an
        // ingress hiccup or a backend hot-reload without failing the
        // poll. If the poll actually fails, `consecutiveFailures` gate
        // still gives up after 3 back-to-back errors so a truly-down
        // backend surfaces within ~90s, not silently forever.
        const r = await axios.get(
          `${API}/api/expansion-intelligence/city-intelligence/seed/jobs/${jobId}`,
          { headers, timeout: 30000 },
        );
        consecutiveFailures = 0;
        const j = r.data?.data || {};
        if (j.stage) setCurrentStage(j.stage);
        if (j.status === 'done' && j.result) {
          // The synchronous endpoint returned `{ok, data: <result>, meta}`.
          // The job stores the engine's raw return (no `ok` wrapper) —
          // hydrate into the same shape consumers expect.
          setSeedResult(j.result);
          setStep('decision');
          try { sessionStorage.removeItem(ACTIVE_JOB_KEY); } catch { /* noop */ }
          setActiveJobId(null);
          return;
        }
        if (j.status === 'failed') {
          const err = j.error || {};
          let msg = err.message || 'The analysis failed unexpectedly.';
          if (err.code === 'llm_seed_failed') {
            msg = 'The candidate-seeding model timed out for this city. ' +
                  'Retry, or try a different city/brief.';
          }
          setSeedError(msg);
          try { sessionStorage.removeItem(ACTIVE_JOB_KEY); } catch { /* noop */ }
          setActiveJobId(null);
          return;
        }
      } catch (err) {
        consecutiveFailures += 1;
        // Three back-to-back poll failures = give up.
        if (consecutiveFailures >= 3) {
          setSeedError('Lost contact with the analysis service. The job may ' +
                        'still be running on the backend — refresh to resume.');
          return;
        }
      }
      await new Promise(res => setTimeout(res, POLL_INTERVAL_MS));
    }
  };

  // ITER269 — Resume an in-flight job on mount if sessionStorage has one.
  useEffect(() => {
    let jobId = null;
    try { jobId = sessionStorage.getItem(ACTIVE_JOB_KEY); } catch { /* noop */ }
    if (!jobId || !API) return;
    setActiveJobId(jobId);
    setStep('discovering');
    setSeedStartedAt(Date.now());
    pollJob(jobId, Date.now());
  }, []);

  const submitSeed = async () => {
    setStep('discovering');
    setSeedResult(null);
    setSeedError(null);
    setCurrentStage('queued');
    const startedAt = Date.now();
    setSeedStartedAt(startedAt);

    // BRIEF ISOLATION FIX — persist the current form to `ei_briefs`
    // BEFORE seeding, so any downstream reader that keys off the
    // saved brief sees the CURRENT run rather than a stale one from
    // a previous session. Best-effort — a save failure does not
    // block the analysis; the inline brief is authoritative anyway.
    try {
      await axios.post(
        `${API}/api/expansion-intelligence/brief`,
        {
          business_objective:  form.business_objective.trim(),
          preferred_cuisines:  form.preferred_cuisines,
          preferred_formats:   form.preferred_formats,
          price_for_two:       form.price_for_two === '' ? null : Number(form.price_for_two),
          target_segments:     form.target_segments,
          target_audience_description: form.target_audience_description || null,
          tier:                form.tier || null,
          format:              form.format || null,
          dietary:             form.dietary,
        },
        { headers, timeout: 15000 },
      );
    } catch { /* non-fatal */ }

    const body = {
      city: form.city.trim(),
      brief: {
        business_objective:  form.business_objective.trim(),
        preferred_cuisines:  form.preferred_cuisines,
        preferred_formats:   form.preferred_formats,
        price_for_two:       form.price_for_two === '' ? null : Number(form.price_for_two),
        target_segments:     form.target_segments,
        target_audience_description: form.target_audience_description || null,
        // 4-Axis Addendum
        tier:                form.tier || null,
        format:              form.format || null,
        dietary:             form.dietary,
      },
      use_saved_brief: true,
      max_candidates: 12,
      validation_radius_m: 1500,
      min_fnb_count: 3,
      api_budget_usd: 4.0,
      // Client-specified preferred areas — per-run only.
      preferred_areas: (form.preferred_areas || []).slice(0, PREFERRED_AREAS_MAX),
    };
    try {
      // ITER269 — kick off async job (returns in <100ms on the happy
      // path). Timeout kept generous (2 min) so a temporary ingress
      // slowdown or a backend restart mid-request never surfaces to
      // the user as "engine couldn't complete the discovery — timeout
      // of 10000ms exceeded". The backend never blocks on the run
      // itself; this timeout only guards the `queued`-row insert.
      const r = await axios.post(
        `${API}/api/expansion-intelligence/city-intelligence/seed/start`,
        body, { headers, timeout: 120000 },
      );
      const job = r.data?.data;
      if (!job?.job_id) {
        throw new Error('Failed to create analysis job.');
      }
      setActiveJobId(job.job_id);
      try { sessionStorage.setItem(ACTIVE_JOB_KEY, job.job_id); } catch { /* noop */ }
      // Poll until done/failed.
      pollJob(job.job_id, startedAt);
    } catch (err) {
      const msg = err?.response?.data?.detail?.message
                  || err?.response?.data?.detail
                  || err?.message
                  || 'Unknown error';
      setSeedError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const handoffToSiteIntel = (candidate) => {
    // ITER264 — Push the FULL Expansion Brief + selected neighbourhood
    // into the shared session so the Site Intelligence workspace can
    // hydrate every field, not just the address. The legacy `setBrief`
    // state is unchanged — this is client-only continuity.
    const city = seedResult?.city || form.city || '';
    const address = `${candidate.name}, ${city}`.trim().replace(/^,\s*/, '');
    mergeSession({
      origin: 'city_intel',
      brief: {
        business_objective:          form.business_objective,
        preferred_cuisines:          form.preferred_cuisines,
        preferred_formats:           form.preferred_formats,
        price_for_two:               form.price_for_two === '' ? null : Number(form.price_for_two),
        target_segments:             form.target_segments,
        target_audience_description: form.target_audience_description,
        // 4-Axis Addendum
        tier:                        form.tier,
        format:                      form.format,
        dietary:                     form.dietary,
      },
      city,
      address,
      selected_neighbourhood: {
        name: candidate.name,
        lat:  candidate.lat,
        lng:  candidate.lng,
        rank: candidate.rank,
        composite_score:  candidate.composite_score,
        opportunity_type: candidate.recommendation?.opportunity_type,
      },
      candidates:           seedResult?.candidates || [],
      rejected:             seedResult?.rejected   || [],
      truncated:            seedResult?.truncated  || [],
      seed_meta:            seedResult?.meta       || {},
      seed_llm:             seedResult?.llm        || {},
      current_stage:        'detail',
    });
    // The legacy EI Home consumes the same session on mount — no need
    // for the older `ei_prefill_address` key anymore.
    try { sessionStorage.setItem('ei_gate_passed', '1'); } catch { /* noop */ }
    navigate('/admin/expansion-intelligence');
  };

  return (
    <div className="min-h-screen bg-slate-50/40">
      <div className="max-w-6xl mx-auto p-6 sm:p-10" data-testid="city-intel-page">
        <Hero stepIndex={stepIndex < 0 ? 0 : stepIndex} />
        {step === 'concept' && (
          <ConceptDiscoveryScreen
            form={form} setForm={setForm}
            onSubmit={submitSeed}
            savedBrief={savedBrief}
            taxonomy={taxonomy}
            onClearBrief={() => {
              // ITER283c-fix BUG 3 — Explicit clear flushes ALL brief
              // fields to empty and purges sessionStorage. No merge
              // with the saved server brief will happen because we
              // wipe savedBrief too — the useEffect that re-hydrates
              // from the server brief only runs on mount.
              clearSession();
              setForm(EMPTY_FORM);
              setSavedBrief(null);
            }}
          />
        )}
        {step === 'discovering' && (
          <DiscoveryProgressScreen
            requestStartedAt={seedStartedAt}
            requestDone={!!seedResult}
            requestError={seedError}
            onRetry={submitSeed}
            onBack={() => { setSeedError(null); setStep('concept'); }}
          />
        )}
        {step === 'decision' && seedResult && (
          <DecisionJourneyScreen
            result={seedResult}
            onContinue={() => setStep('explorer')}
            onBack={() => setStep('concept')}
            onSelectCandidate={(c) => { setSelectedCandidate(c); setStep('detail'); }}
          />
        )}
        {step === 'explorer' && seedResult && (
          <OpportunityExplorerScreen
            result={seedResult}
            onSelect={(c) => { setSelectedCandidate(c); setStep('detail'); }}
            onAnalyse={(c) => handoffToSiteIntel(c)}
            onBack={() => setStep('decision')}
          />
        )}
        {step === 'detail' && selectedCandidate && (
          <NeighbourhoodDetailScreen
            candidate={selectedCandidate}
            city={seedResult?.city || form.city}
            brief={form}
            allCandidates={seedResult?.candidates || []}
            onBack={() => setStep('explorer')}
            onAnalyse={() => handoffToSiteIntel(selectedCandidate)}
          />
        )}
      </div>
    </div>
  );
}

